import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PlusIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { useNIBSubmission } from '@hooks/useNIBSubmission';
import { nibApi } from '@services/api';
import {
  SubmissionSummary,
  RecordsTable,
  RecordForm,
  ImportModal,
  ValidationResults,
  NIBStatusBadge,
} from '@components/nib';
import { LoadingSpinner, ConfirmModal, EmptyState } from '@components/common';
import { Button } from '@components/Button';
import type {
  NIBEmployeeRecord,
  NIBRecordCreateRequest,
  NIBSourceSystem,
  NIBPayIndicator,
  NIBNewHire,
} from '@/types';

/**
 * NIB Submission Detail Page
 *
 * Displays full submission details with employee records
 * Supports all submission operations:
 * - Add/edit/delete records manually
 * - Import records from payroll files
 * - Validate submission
 * - Generate C10 file
 * - Download generated file
 *
 * Actions available depend on submission status:
 * - DRAFT: Add, Import, Validate
 * - VALIDATED: Generate, Validate Again
 * - GENERATED: Download, Regenerate
 */
export function NIBSubmissionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Hook for submission operations
  const {
    submission,
    isLoading,
    error,
    refresh,
    addRecord,
    updateRecord,
    deleteRecord,
    importRecords,
    mergePayroll,
    validate,
    generate,
    download,
  } = useNIBSubmission(id!);

  // Modal states
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingRecord, setEditingRecord] = useState<
    NIBEmployeeRecord | undefined
  >();
  const [deletingRecordId, setDeletingRecordId] = useState<string | null>(null);

  // Loading states for async operations
  const [isValidating, setIsValidating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Handle add record
  const handleAddRecord = () => {
    setEditingRecord(undefined);
    setShowRecordForm(true);
  };

  // Handle edit record
  const handleEditRecord = (record: NIBEmployeeRecord) => {
    if (!canAddRecord) return; // Only allow editing in DRAFT status
    setEditingRecord(record);
    setShowRecordForm(true);
  };

  // Handle delete record click
  const handleDeleteRecordClick = (record: NIBEmployeeRecord) => {
    if (!canAddRecord) return; // Only allow deleting in DRAFT status
    setDeletingRecordId(record.id);
    setShowDeleteConfirm(true);
  };

  // Confirm delete record
  const handleConfirmDelete = async () => {
    if (!deletingRecordId) return;

    try {
      await deleteRecord(deletingRecordId);
      setShowDeleteConfirm(false);
      setDeletingRecordId(null);
    } catch {
      // Error handled in hook
    }
  };

  // Handle record form submit
  const handleRecordSubmit = async (data: NIBRecordCreateRequest) => {
    try {
      if (editingRecord) {
        await updateRecord(editingRecord.id, data);
      } else {
        await addRecord(data);
      }
      setShowRecordForm(false);
      setEditingRecord(undefined);
    } catch {
      // Error handled in hook
    }
  };

  // Handle import or merge depending on whether records already exist
  const handleImport = async (
    file: File,
    sourceSystem: NIBSourceSystem,
    payIndicator: NIBPayIndicator
  ) => {
    if (hasRecords) {
      const result = await mergePayroll(file, sourceSystem, payIndicator);
      if (result) return result;
      throw new Error('Merge failed');
    }
    const result = await importRecords(file, sourceSystem, payIndicator);
    if (result) {
      setShowImportModal(false);
      return result;
    }
    throw new Error('Import failed');
  };

  // Handle approving new hires discovered during merge
  const handleApproveNewHires = async (
    newHires: NIBNewHire[],
    addToRoster: boolean
  ) => {
    await nibApi.addNewHires(id!, newHires, addToRoster);
    toast.success(`Added ${newHires.length} new employee${newHires.length !== 1 ? 's' : ''}`);
    await refresh();
    setShowImportModal(false);
  };

  // Handle validate
  const handleValidate = async () => {
    setIsValidating(true);
    try {
      await validate();
    } finally {
      setIsValidating(false);
    }
  };

  // Handle generate
  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      await generate();
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle download
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await download();
    } finally {
      setIsDownloading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading submission...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !submission) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Failed to Load Submission
          </h2>
          <p className="text-gray-600 mb-6">
            {error?.message || 'Submission not found'}
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/nib-c10')}>
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to List
            </Button>
            <Button onClick={refresh}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  // Determine available actions based on status
  const canAddRecord = submission.status === 'DRAFT';
  const canImport = submission.status === 'DRAFT';
  const canValidate =
    submission.status === 'DRAFT' || submission.status === 'VALIDATED';
  const canGenerate = submission.status === 'VALIDATED';
  const canRegenerate =
    submission.status === 'GENERATED' || submission.status === 'SUBMITTED';
  const canDownload =
    submission.status === 'GENERATED' || submission.status === 'SUBMITTED';

  const hasRecords = submission.records && submission.records.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nib-c10')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Back to Submissions
          </button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  Submission {submission.contribution_period}
                </h1>
                <NIBStatusBadge status={submission.status} />
              </div>
              <p className="text-sm text-gray-600">
                Employer: {submission.employer_name || 'Unknown'} (
                {submission.employer_number})
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {canDownload && (
                <Button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  variant="primary"
                >
                  {isDownloading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                      Download C10
                    </>
                  )}
                </Button>
              )}

              {canRegenerate && (
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  variant="outline"
                >
                  {isGenerating ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Regenerating...
                    </>
                  ) : (
                    'Regenerate'
                  )}
                </Button>
              )}

              {canGenerate && (
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  variant="primary"
                >
                  {isGenerating ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Generating...
                    </>
                  ) : (
                    'Generate C10'
                  )}
                </Button>
              )}

              {canValidate && (
                <Button
                  onClick={handleValidate}
                  disabled={isValidating}
                  variant={
                    submission.status === 'DRAFT' ? 'primary' : 'outline'
                  }
                >
                  {isValidating ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Validating...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                      {submission.status === 'VALIDATED'
                        ? 'Validate Again'
                        : 'Validate'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div className="mb-6">
          <SubmissionSummary submission={submission} />
        </div>

        {/* Validation Results */}
        {submission.validation_result && (
          <div className="mb-6">
            <ValidationResults
              validationResult={submission.validation_result}
            />
          </div>
        )}

        {/* Records Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Employee Records
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {submission.total_records} record
                  {submission.total_records !== 1 ? 's' : ''}
                </p>
              </div>

              <div className="flex gap-2">
                {canImport && (
                  <Button
                    onClick={() => setShowImportModal(true)}
                    variant="outline"
                  >
                    <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
                    {hasRecords ? 'Merge Wages' : 'Import Records'}
                  </Button>
                )}

                {canAddRecord && (
                  <Button onClick={handleAddRecord} variant="primary">
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Add Record
                  </Button>
                )}
              </div>
            </div>
          </div>

          {hasRecords ? (
            <RecordsTable
              records={submission.records}
              onEdit={handleEditRecord}
              onDelete={handleDeleteRecordClick}
            />
          ) : (
            <div className="p-12">
              <EmptyState
                title="No Employee Records"
                description="Add employee records manually or import from a payroll file"
              />
              {canAddRecord && (
                <div className="flex gap-3 justify-center mt-6">
                  <Button onClick={handleAddRecord}>
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Add Record
                  </Button>
                  <Button
                    onClick={() => setShowImportModal(true)}
                    variant="outline"
                  >
                    <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
                    Import Records
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modals */}
        {showRecordForm && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              <div
                className="fixed inset-0 bg-black bg-opacity-30"
                onClick={() => {
                  setShowRecordForm(false);
                  setEditingRecord(undefined);
                }}
              />
              <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
                <RecordForm
                  onSubmit={handleRecordSubmit}
                  onCancel={() => {
                    setShowRecordForm(false);
                    setEditingRecord(undefined);
                  }}
                  initialData={editingRecord}
                  formatType={submission.format_type}
                />
              </div>
            </div>
          </div>
        )}

        {showImportModal && (
          <ImportModal
            isOpen={showImportModal}
            onClose={() => {
              setShowImportModal(false);
              refresh();
            }}
            onImport={handleImport}
            mode={hasRecords ? 'merge' : 'import'}
            onApproveNewHires={handleApproveNewHires}
          />
        )}

        {showDeleteConfirm && (
          <ConfirmModal
            isOpen={showDeleteConfirm}
            onClose={() => {
              setShowDeleteConfirm(false);
              setDeletingRecordId(null);
            }}
            onConfirm={handleConfirmDelete}
            title="Delete Record"
            message="Are you sure you want to delete this employee record? This action cannot be undone."
            confirmLabel="Delete"
            variant="danger"
          />
        )}
      </div>
    </div>
  );
}

export default NIBSubmissionDetail;
